/**
 * PLAT-11445 — audio-first Clip Editor: local canvas + rendition hand-off.
 *
 * A clip opened while the story's renditions are still producing carries an
 * audio-first composition (its media element sources the editor mp3 — the
 * canvas paints black). This hook owns the two mid-session swaps that turn
 * that into a playing picture, and the veil that hides the black canvas in
 * between:
 *
 *   1. Local canvas — when this session still holds the uploaded File and the
 *      browser can decode it, push it into the clip renderer's cache and
 *      re-point the composition at it (adapter.upgradeToLocalCanvas). One
 *      attempt per story; an undecodable file stays veiled.
 *   2. Rendition hand-off — when the pending flag flips in this session, wait
 *      for the preview rendition to be fetchable (waitForRenditionFetchable,
 *      the renderer's own fetch — populate lists it before the bytes land),
 *      then swap the composition onto it (adapter.upgradeToRendition). No
 *      save: the media is resolved at open, so a reload re-resolves anyway.
 *
 * Both swaps run under the renderer hook's `isLoading` veil (host hidden,
 * cleared by the renderer's own onLoadComplete), so the canvas never shows the
 * black gap between "source swapped" and "new media painted".
 *
 * Creator Studio counterpart: the readiness poll + rendition-landed effect in
 * VideoCreator.tsx over VideoCreatorStore's audio-first steps.
 */
import {
  probeVideoDecodable,
  requiresNativeLocalPreview,
  waitForRenditionFetchable,
} from '@src/services/LocalCanvasPreview';
import { WarmedRenderer } from '@src/services/WarmedRenderer';
import { useUserStoryUploadFlow } from '@src/stores-v2/StoreContext';
import { pickRenditionUrl } from '@src/utility/clipMediaSource';
import { LogManager } from '@src/utility/Logger';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { useEffect, useRef, useState } from 'react';

import type { ClipVideoCreatorAdapter } from './ClipVideoCreatorAdapter';
import type { Renderer } from '@src/renderer/Renderer';
import type { Story } from '@src/types.ts/story';
import type { CompositionMediaKind } from '@src/utility/audioFirstSwap';

const logger = LogManager.getLogger('hook:useClipAudioFirstCanvas');

export type ClipAudioFirstPhase = 'idle' | 'local-canvas' | 'hand-off';

export interface UseClipAudioFirstCanvasOptions {
  adapter: ClipVideoCreatorAdapter | null | undefined;
  renderer: Renderer | null | undefined;
  story: Story | null | undefined;
  /** Renderer hook's veil state — swaps run under it. */
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  /**
   * Renderer hook's host-hide flag: the prewarmed iframe host floats above
   * the modal, so the processing veil must hide it from the hook's side.
   */
  setCanvasHidden?: (hidden: boolean) => void;
  enabled?: boolean;
}

export interface ClipAudioFirstState {
  /** Composition is audio-first and settled: hide the black canvas. */
  processingVeil: boolean;
  /** Templates preview branding over the real video — none yet. */
  templatesLocked: boolean;
  mediaKind: CompositionMediaKind;
  phase: ClipAudioFirstPhase;
  renditionPending: boolean;
  nativeLocalPreviewUrl: string | null;
}

function warmWith(renderer: Renderer | null | undefined) {
  return renderer instanceof WarmedRenderer
    ? (url: string) => renderer.warmAsset(url)
    : null;
}

export function useClipAudioFirstCanvas({
  adapter,
  renderer,
  story,
  isLoading,
  setIsLoading,
  setCanvasHidden,
  enabled = true,
}: UseClipAudioFirstCanvasOptions): ClipAudioFirstState {
  const uploadFlow = useUserStoryUploadFlow();
  const storyId = story?.id;
  const renditionPending = isCompressedRenditionPending(story);
  const renditionUrl = pickRenditionUrl(story?.originalVideo?.video);

  const [mediaKind, setMediaKind] = useState<CompositionMediaKind>('none');
  const [phase, setPhase] = useState<ClipAudioFirstPhase>('idle');
  const [tick, setTick] = useState(0);
  const [nativeLocalPreview, setNativeLocalPreview] = useState<{
    storyId: string;
    url: string;
  } | null>(null);
  const localAttemptStoryRef = useRef<string | null>(null);
  const wasPendingRef = useRef(false);

  // Classify the composition whenever a load settles or the story moves.
  useEffect(() => {
    if (!enabled || !adapter || isLoading) return;
    setMediaKind(adapter.compositionMediaKind());
  }, [enabled, adapter, isLoading, renditionPending, storyId, tick]);

  // 1. Local canvas
  useEffect(() => {
    if (!enabled || !adapter || !renderer || !storyId || isLoading) return;
    if (!renditionPending || mediaKind !== 'audio-first') return;
    if (localAttemptStoryRef.current === storyId) return;
    const file = uploadFlow.localVideoFileRecord?.[storyId];
    const objectUrl = uploadFlow.getLocalVideoUrl?.(storyId);
    if (!file || !objectUrl) return;
    localAttemptStoryRef.current = storyId;
    let cancelled = false;
    (async () => {
      setPhase('local-canvas');
      try {
        const decodable = await probeVideoDecodable(objectUrl);
        if (cancelled) return;
        if (!decodable) {
          logger.debug('Local canvas skipped: file not browser-decodable', {
            storyId,
          });
          return;
        }
        if (requiresNativeLocalPreview(file)) {
          setNativeLocalPreview({ storyId, url: objectUrl });
          logger.info('Using native local preview for oversized upload', {
            storyId,
            bytes: file.size,
          });
          return;
        }
        setIsLoading(true);
        const applied = await adapter.upgradeToLocalCanvas(file);
        if (!applied) setIsLoading(false);
        logger.debug('Local canvas', { storyId, applied });
      } catch (error) {
        setIsLoading(false);
        logger.warn('Local canvas failed — staying audio-first', {
          storyId,
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        if (!cancelled) {
          setPhase('idle');
          setTick(t => t + 1);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    adapter,
    renderer,
    storyId,
    isLoading,
    renditionPending,
    mediaKind,
    uploadFlow,
    setIsLoading,
  ]);

  // 2. Rendition hand-off
  useEffect(() => {
    if (renditionPending) {
      wasPendingRef.current = true;
      return undefined;
    }
    if (!wasPendingRef.current || !enabled || !adapter || !storyId) {
      return undefined;
    }
    if (mediaKind === 'video' || mediaKind === 'none') {
      wasPendingRef.current = false;
      return undefined;
    }
    if (!renditionUrl || isLoading) return undefined;
    const controller = new AbortController();
    (async () => {
      setPhase('hand-off');
      const warm = warmWith(renderer);
      const fetchable = warm
        ? await waitForRenditionFetchable(warm, renditionUrl, {
            signal: controller.signal,
          })
        : true;
      if (controller.signal.aborted) return;
      if (!fetchable) {
        logger.warn('Rendition never became fetchable — staying as is', {
          storyId,
        });
        setPhase('idle');
        return;
      }
      setIsLoading(true);
      const applied = adapter.upgradeToRendition(renditionUrl);
      if (!applied) setIsLoading(false);
      wasPendingRef.current = false;
      logger.debug('Rendition hand-off', { storyId, applied });
      setPhase('idle');
      setTick(t => t + 1);
    })();
    return () => controller.abort();
  }, [
    renditionPending,
    renditionUrl,
    enabled,
    adapter,
    renderer,
    storyId,
    mediaKind,
    isLoading,
    setIsLoading,
  ]);

  const nativeLocalPreviewUrl =
    nativeLocalPreview &&
    enabled &&
    renditionPending &&
    nativeLocalPreview.storyId === storyId
      ? nativeLocalPreview.url
      : null;
  const processingVeil =
    enabled &&
    !isLoading &&
    mediaKind === 'audio-first' &&
    !nativeLocalPreviewUrl;
  useEffect(() => {
    setCanvasHidden?.(processingVeil || !!nativeLocalPreviewUrl);
    return () => setCanvasHidden?.(false);
  }, [processingVeil, nativeLocalPreviewUrl, setCanvasHidden]);

  return {
    processingVeil,
    templatesLocked: enabled && (renditionPending || mediaKind !== 'video'),
    mediaKind,
    phase,
    renditionPending,
    nativeLocalPreviewUrl,
  };
}
